/**
 * Read a JSON API response without losing the reason when it is not JSON.
 *
 * `await res.json()` on a body the platform wrote — Vercel's plain-text
 * "An error occurred with this application." for a crashed or timed-out
 * function, a proxy's HTML 502 — throws a `SyntaxError` whose message is the
 * first ten characters of that body and nothing else. The status code, the
 * route and the rest of the body are all gone by the time the `catch` runs, so
 * the error row it produces cannot be traced back to an endpoint.
 *
 * Reading the text first and parsing it ourselves keeps all three.
 */

/** How much of a non-JSON body to carry into the error message. */
const BODY_PREFIX_LIMIT = 200;

/** Statuses that mean "you are not signed in", not "something broke". */
const SIGNED_OUT_STATUSES = [401, 403];

export interface ReadJsonOptions {
  /**
   * Resolve a 401/403 to `null` instead of reading its body.
   *
   * An endpoint behind a session answers a signed-out caller with a 401, and
   * the platform is free to send that with no body at all — which parses to
   * nothing and gets reported as a fault. On a page every visitor can request
   * but only a member can use, being signed out is an expected state, so the
   * caller gets `null` and reports nothing.
   */
  signedOutAsNull?: boolean;
}

/**
 * A response that was supposed to be JSON and was not.
 *
 * Thrown instead of `SyntaxError` so callers can tell "the server returned
 * something that is not our API" apart from "the request never arrived", which
 * are different things to tell the person waiting on it.
 */
export class NonJsonResponseError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly bodyPrefix: string;
  readonly endpoint: string;
  /** The HTML page's <title>, or null when the body was not an HTML page. */
  readonly pageTitle: string | null;
  /** The `answered-by` token from `describeRefusal`, or null when none was recorded. */
  readonly answeredBy: string | null;

  /**
   * @param body    what came back instead of JSON; an HTML page is read whole
   *                for its <title>, and only the start is kept
   * @param refusal low-cardinality tokens naming who answered, from
   *                `describeRefusal`; empty when there is nothing to say
   */
  constructor(endpoint: string, status: number, statusText: string, body: string, refusal = '') {
    const bodyPrefix = body.trim().slice(0, BODY_PREFIX_LIMIT);
    const page = describeHtmlPage(body.trim());
    super(
      `${endpoint} returned a non-JSON body (HTTP ${status}${
        statusText ? ` ${statusText}` : ''
      }): ${page?.summary ?? (bodyPrefix || '<empty>')}${refusal ? ` [${refusal}]` : ''}`
    );
    this.name = 'NonJsonResponseError';
    this.endpoint = endpoint;
    this.status = status;
    this.statusText = statusText;
    this.bodyPrefix = bodyPrefix;
    this.pageTitle = page?.title ?? null;
    this.answeredBy = /answered-by=(\S+)/.exec(refusal)?.[1] ?? null;
  }
}

const PAGE_TITLE_LIMIT = 80;

/**
 * An HTML body summarised by its <title>, which names who wrote the page
 * ("403: Forbidden" from Vercel, "Web Page Blocked", "Access Denied"). The
 * first 200 characters of a page are its doctype and meta tags -- production
 * rows 2026-09-27 (clusters 44bba35305b61c7b, a6d22cc101ef72f9) carried
 * `<!DOCTYPE html> <html lang="en" data-bs-theme="light">` and nothing that
 * said which filter refused the visitor. Title only: the body of a refusal page
 * can carry the visitor's IP, and any IPv4 in the title is masked too.
 */
function describeHtmlPage(body: string): { title: string | null; summary: string } | null {
  if (!/^<(!doctype html|html)[\s>]/i.test(body)) return null;
  const match = /<title[^>]*>([^<]*)<\/title>/i.exec(body);
  const title =
    match?.[1]
      ?.replace(/\s+/g, ' ')
      .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>')
      .trim()
      .slice(0, PAGE_TITLE_LIMIT) || null;
  return { title, summary: title ? `html page "${title}"` : 'html page, no <title>' };
}

/**
 * Who answered a 401/403, from the response headers. A non-JSON refusal was
 * written in front of our code: the Vercel firewall (`x-vercel-mitigated`), the
 * Vercel edge with no function behind it, or -- with no `x-vercel-id` at all --
 * something between the visitor and Vercel. Low-cardinality tokens only: the
 * request id itself would split the cluster into one row each.
 */
function describeRefusal(res: Response): string {
  if (!SIGNED_OUT_STATUSES.includes(res.status) || typeof res.headers?.get !== 'function') {
    return '';
  }
  // One region per hop: "iad1::<id>" was answered at the edge,
  // "iad1::iad1::<id>" reached a function.
  const vercelId = res.headers.get('x-vercel-id');
  const answeredBy = vercelId
    ? vercelId.split('::').length > 2
      ? 'function'
      : 'vercel-edge'
    : 'not-vercel';
  return `mitigated=${res.headers.get('x-vercel-mitigated') ?? 'none'} answered-by=${answeredBy}`;
}

export interface RefusedResponseDescription {
  /** `describeRefusal`'s tokens, or 'none' for a status other than 401/403. */
  refusal: string;
  /** The `server` header ("gunicorn", "nginx"), IPv4s masked; 'none' when absent. */
  server: string;
  /** 'json', 'html', 'other', or 'none' when there was no content-type. */
  body: string;
}

/**
 * Who answered a failed API call, for callers that log the status themselves
 * instead of parsing the body. A bare `{ status: 403 }` cannot say whether
 * Vercel refused the visitor or a filter on their network did: on 2026-10-21
 * one Firefox visitor's tag-fallback rows named a `gunicorn` hop with no
 * `x-vercel-id`, while the slideshow row from the same session
 * (cluster 13e231ae26c19308) carried the status and nothing else.
 */
export function describeRefusedResponse(res: Response): RefusedResponseDescription {
  const header = (name: string): string =>
    typeof res.headers?.get === 'function' ? (res.headers.get(name) ?? '') : '';
  const contentType = header('content-type');
  return {
    refusal: describeRefusal(res) || 'none',
    server:
      header('server')
        .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>')
        .trim()
        .slice(0, PAGE_TITLE_LIMIT) || 'none',
    body: contentType.includes('json')
      ? 'json'
      : contentType.includes('html')
        ? 'html'
        : contentType
          ? 'other'
          : 'none',
  };
}

/**
 * Parse a fetch response as JSON, or throw a `NonJsonResponseError` naming the
 * endpoint, the status and the start of what came back instead.
 *
 * @param res      the response to read
 * @param endpoint a stable label for the call, e.g. `POST /api/user/change-username`
 * @param options  `signedOutAsNull` to treat a 401/403 as an expected state
 */
export async function readJsonResponse<T>(
  res: Response,
  endpoint: string,
  options: ReadJsonOptions = {}
): Promise<T | null> {
  if (options.signedOutAsNull && SIGNED_OUT_STATUSES.includes(res.status)) return null;

  const body = await res.text();

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new NonJsonResponseError(
      endpoint,
      res.status,
      res.statusText,
      body,
      describeRefusal(res)
    );
  }
}

/**
 * Parse a fetch response through `res.json()`, and when that throws the
 * `SyntaxError` a non-JSON body produces, rethrow a `NonJsonResponseError`
 * naming the endpoint and the status instead.
 *
 * `readJsonResponse` reads the body as text first, which is the better shape
 * for a new callsite. This one exists for the hooks whose callers and tests
 * hand the response straight to `json()`: it keeps that contract while
 * stopping the row from being a bare `Unexpected token '<'` with no status
 * and no route — which is what an edge 403 page parsed as JSON looks like.
 *
 * The body prefix comes from a clone taken before parsing, when the response
 * supports one; a stand-in without `clone` still gets the status and endpoint.
 */
export function parseJsonResponse<T>(res: Response, endpoint: string): Promise<T> {
  const probe = typeof res.clone === 'function' ? res.clone() : null;

  return res.json().then(
    (data) => data as T,
    async (err: unknown) => {
      if (!isJsonParseFailure(err)) throw err;

      let body = '';
      if (probe) {
        try {
          body = await probe.text();
        } catch {
          // The body could not be read twice; the status is still the finding.
        }
      }

      throw new NonJsonResponseError(
        endpoint,
        res.status,
        res.statusText,
        body,
        describeRefusal(res)
      );
    }
  );
}

/**
 * True when `res.json()` rejected because the body was not JSON.
 *
 * Chrome and Firefox reject with a JS `SyntaxError`. Safari rejects with a
 * `DOMException` named `SyntaxError` ("The string did not match the expected
 * pattern."), which is not an instance of `SyntaxError` -- so an `instanceof`
 * check let every Safari parse failure through bare, with no status and no
 * route, and at error instead of the warning an edge refusal gets.
 */
function isJsonParseFailure(err: unknown): boolean {
  if (err instanceof SyntaxError) return true;
  return (
    typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'SyntaxError'
  );
}

/**
 * True when a non-JSON body arrived with a 401/403.
 *
 * Every API route on this app answers in JSON, so a non-JSON forbidden page
 * was written by the edge or the platform before our code ran. That is worth
 * a row, but not at `error`: it is a refused visitor, not an application fault.
 */
export function isEdgeRefusal(err: unknown): err is NonJsonResponseError {
  return err instanceof NonJsonResponseError && SIGNED_OUT_STATUSES.includes(err.status);
}

/**
 * True when a 401/403 was written by something between the visitor and Vercel:
 * no `x-vercel-id` on the response. A corporate or ISP proxy blocking our API
 * is the visitor's network, not ours and not Vercel's, so callers report it at
 * info. Rows with no recorded `answered-by` stay an ordinary edge refusal.
 */
export function isForeignRefusal(err: unknown): boolean {
  return isEdgeRefusal(err) && err.answeredBy === 'not-vercel';
}

/**
 * True when `fetch` itself rejected: the request never got an answer.
 *
 * Browsers reject with a bare `TypeError` -- "Failed to fetch" (Chrome),
 * "Load failed" (Safari), "NetworkError when attempting to fetch resource"
 * (Firefox) -- for a dropped connection, a navigation that cancelled the
 * request, or an extension blocking it. The visitor's network is the cause,
 * not our API, so callers report it at warning.
 */
export function isNetworkFailure(err: unknown): boolean {
  return (
    err instanceof TypeError &&
    /^(failed to fetch|load failed|networkerror when attempting to fetch resource)/i.test(
      err.message
    )
  );
}

export default readJsonResponse;
